import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button, Checkbox, Input, Select, SelectItem, DateField } from '@structural-climbing/ui'
import type { DeskPaymentMethod } from '../invoices/paymentMethod'
import { GymDialog } from '../../components/GymDialog'

interface BillingForm {
  billingType: 'individual' | 'company'
  firstName: string
  lastName: string
  cnp: string
  companyName: string
  cui: string
  regCom: string
  address: string
  city: string
  county: string
}

const emptyBilling = (): BillingForm => ({
  billingType: 'individual',
  firstName: '',
  lastName: '',
  cnp: '',
  companyName: '',
  cui: '',
  regCom: '',
  address: '',
  city: '',
  county: '',
})

interface Props {
  subscriptionId: string
  memberEmail: string
  /** Two accounts can share an email, so the order attaches by id when we have one. */
  memberUserId?: string | null
  planName: string
  defaultAmount: number
  defaultDate?: string
  currency: string
  onCreated: () => void
  onClose: () => void
}

export function CreateInvoiceModal({
  subscriptionId,
  memberEmail,
  memberUserId,
  planName,
  defaultAmount,
  defaultDate,
  currency,
  onCreated,
  onClose,
}: Props) {
  const today = new Date().toISOString().slice(0, 10)
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [paymentMethod, setPaymentMethod] = useState<DeskPaymentMethod>('cash')
  const [amount, setAmount] = useState(defaultAmount.toFixed(2))
  const [date, setDate] = useState(defaultDate ?? today)
  const [issueInvoice, setIssueInvoice] = useState(false)
  const [billing, setBilling] = useState<BillingForm>(emptyBilling())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function setBillingField(field: keyof BillingForm, value: string) {
    setBilling((prev) => ({ ...prev, [field]: value }))
  }

  async function handleCreate() {
    const parsed = parseFloat(amount)
    if (isNaN(parsed) || parsed < 0) {
      setError(t('createInvoice.invalidAmount'))
      return
    }
    setLoading(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const body: Record<string, unknown> = {
        subscription_id: subscriptionId,
        member_email: memberEmail,
        ...(memberUserId ? { member_user_id: memberUserId } : {}),
        payment_method: paymentMethod,
        total_amount: parsed,
        plan_name: planName,
        created_at: date ? new Date(date).toISOString() : undefined,
      }
      if (issueInvoice && billing.billingType) {
        body.billing = {
          billing_type: billing.billingType,
          first_name: billing.firstName || undefined,
          last_name: billing.lastName || undefined,
          cnp: billing.cnp || undefined,
          company_name: billing.companyName || undefined,
          cui: billing.cui || undefined,
          reg_com: billing.regCom || undefined,
          address: billing.address || undefined,
          city: billing.city || undefined,
          county: billing.county || undefined,
        }
      }
      const res = await fetch(`${apiUrl}/invoices`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        setError(t('createInvoice.error'))
        return
      }
      onCreated()
    } catch {
      setError(t('createInvoice.error'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <GymDialog onClose={onClose} size="sm">
      <div className="overflow-y-auto p-6">
        <h2 className="mb-1 text-base font-semibold">{t('createInvoice.title')}</h2>
        <p className="mb-5 text-xs text-foreground/50">
          {t('createInvoice.subtitle', { plan: planName, email: memberEmail })}
        </p>

        <div className="flex flex-col gap-4">
          <Select
            label={t('assignPlan.paymentMethodLabel')}
            selectedKeys={[paymentMethod]}
            onSelectionChange={(keys) => {
              const val = String(Array.from(keys)[0] ?? 'cash') as DeskPaymentMethod
              setPaymentMethod(val)
            }}
          >
            <SelectItem key="cash">{t('assignPlan.cash')}</SelectItem>
            <SelectItem key="card">{t('assignPlan.card')}</SelectItem>
            <SelectItem key="transfer">{t('assignPlan.transfer')}</SelectItem>
          </Select>

          <DateField label={t('createInvoice.dateLabel')} value={date} onValueChange={setDate} />

          <Input
            label={t('assignPlan.pricePaidLabel')}
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onValueChange={setAmount}
            startContent={<span className="text-sm text-foreground/50">{currency}</span>}
          />

          <Checkbox isSelected={issueInvoice} onValueChange={setIssueInvoice}>
            {t('assignPlan.issueInvoice')}
          </Checkbox>

          {issueInvoice && (
            <div className="flex flex-col gap-3 rounded-lg border border-divider p-4">
              <Select
                label={t('assignPlan.billingTypeLabel')}
                selectedKeys={[billing.billingType]}
                onSelectionChange={(keys) => {
                  const val = String(Array.from(keys)[0] ?? 'individual') as
                    | 'individual'
                    | 'company'
                  setBilling((prev) => ({ ...prev, billingType: val }))
                }}
              >
                <SelectItem key="individual">{t('assignPlan.individual')}</SelectItem>
                <SelectItem key="company">{t('assignPlan.company')}</SelectItem>
              </Select>

              {billing.billingType === 'individual' ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label={t('assignPlan.firstName')}
                      value={billing.firstName}
                      onValueChange={(v) => setBillingField('firstName', v)}
                    />
                    <Input
                      label={t('assignPlan.lastName')}
                      value={billing.lastName}
                      onValueChange={(v) => setBillingField('lastName', v)}
                    />
                  </div>
                  <Input
                    label={t('assignPlan.cnp')}
                    value={billing.cnp}
                    onValueChange={(v) => setBillingField('cnp', v)}
                  />
                </>
              ) : (
                <>
                  <Input
                    label={t('assignPlan.companyName')}
                    value={billing.companyName}
                    onValueChange={(v) => setBillingField('companyName', v)}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label={t('assignPlan.cui')}
                      value={billing.cui}
                      onValueChange={(v) => setBillingField('cui', v)}
                    />
                    <Input
                      label={t('assignPlan.regCom')}
                      value={billing.regCom}
                      onValueChange={(v) => setBillingField('regCom', v)}
                    />
                  </div>
                </>
              )}

              <Input
                label={t('assignPlan.address')}
                value={billing.address}
                onValueChange={(v) => setBillingField('address', v)}
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label={t('assignPlan.city')}
                  value={billing.city}
                  onValueChange={(v) => setBillingField('city', v)}
                />
                <Input
                  label={t('assignPlan.county')}
                  value={billing.county}
                  onValueChange={(v) => setBillingField('county', v)}
                />
              </div>
            </div>
          )}

          {error && <p className="text-sm text-danger">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button size="sm" variant="flat" onClick={onClose} isDisabled={loading}>
              {t('common.cancel')}
            </Button>
            <Button
              size="sm"
              color="primary"
              isLoading={loading}
              onClick={() => void handleCreate()}
            >
              {t('createInvoice.create')}
            </Button>
          </div>
        </div>
      </div>
    </GymDialog>
  )
}
